import React, {
  memo,
  useCallback,
  useEffect,
  useState,
  useRef,
  forwardRef
} from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Text } from 'grommet';
import { CaretDownFill, CaretUpFill } from 'grommet-icons';
import { orderBy } from 'lodash';
import SimpleTable from '../components/simple-table';
import {
  getItemNames,
  getItemInfo,
  getPageTypes,
  formatCurrency,
  formatNumber,
  getSourceInfo,
  generateNewId,
  getSubVerticalNames,
  getPlacementExperienceNames,
  getPlacementLocationNames
} from '../utils';
import {
  getInterval,
  getBounds,
  getRange,
  getElasticTimeZoneOffset,
  getComparisonRange,
  getComparisonBounds,
  getFormattedDate,
  createContextEventUrl,
  elasticComparisonToRange,
  NESTED_FIELDS,
  getFlattenedEventMapping
} from '../reporting-utils';
import {
  groupColumn,
  getGroupProperty,
  getGroupSortProperty,
  ITEM_GROUP_FIELDS
} from '../flows/flow-report-utils';
import { getSortedGroupRows } from '../flows/flow-report-group-utils';

import {
  METRICS,
  FORMULAS,
  COPY_DOWN_METRICS,
  getDependencies,
  dataSources
} from './metrics';
import { buildPathForitem } from '../routing-utils';
import { TRAFFIC_SOURCE } from '../items/item-types';
import { getTermsCDSData } from './terms-report';
import { getCompositeCDSData } from './composite-report';
import { IMPLEMENTATION_TYPES } from '../traffic/traffic-partners/embedded-flow-config/embedded-flow-config-info';
import { SORT_STRATEGY_NAMES } from '../campaigns/placement/campaign-placement.constants';

export const NO_VALUE = 'No value';

const DO_NOT_SHOW_URL_PATH = [
  // these are backend entities that don't have a url path in the frontend
  'campaignVerticalId',
  'campaignSubVerticalId',
  'sourceVerticalId',
  'sourceSubVerticalId',
  'businessUnitId',
  'subPartnerVerticalId',
  'subPartnerSubVerticalId',
  'placementExperienceId',
  'placementLocationId'
];

const getGridRowParent = (gridRowKey, reportData) => {
  const keyParts = gridRowKey.split('.');
  keyParts.splice(-1, 1, 'All');
  const parentKey = keyParts.join('.');

  return reportData.find(row => row.key === parentKey);
};

const applyDerivedMetricsToRow = (measures, row, parentSubTotalRow) => {
  measures
    .filter(m => m.formula)
    .forEach(m => {
      if (typeof FORMULAS[m.formula] === 'function') {
        if (
          parentSubTotalRow &&
          COPY_DOWN_METRICS.some(({ metrics }) => metrics.includes(m.property))
        ) {
          row[m.property] = FORMULAS[m.formula](
            Array.isArray(m.requiredProperties)
              ? m.requiredProperties.map(rp => parentSubTotalRow[rp])
              : []
          );
        } else {
          row[m.property] = FORMULAS[m.formula](
            Array.isArray(m.requiredProperties)
              ? m.requiredProperties.map(rp => row[rp])
              : []
          );
        }
      } else if (typeof m.formula === 'function') {
        row[m.property] = m.formula(row);
      } else {
        row[m.property] = 0;
      }
    });
};

export const resolveDependencies = async (
  fields,
  measureAggs,
  paramCache,
  fieldsToGrab,
  parentDepth = 0
) => {
  //resolve external dependencies of fields
  await Promise.all(
    fields
      .filter(f => typeof f.resolveParams === 'function')
      .map(f => f.resolveParams(paramCache))
  );

  for (let i = 0; i < fields.length; i++) {
    const m = fields[i];
    fieldsToGrab.push({
      depth: parentDepth,
      field: m
    });
    if (m.aggs) {
      measureAggs[m.property] = m.aggs;
    } else if (typeof m.generateAggs === 'function') {
      measureAggs[m.property] = m.generateAggs(paramCache);
    } else if (Array.isArray(m.requiredProperties)) {
      //grab base fields for derived fields
      //purposefully overwrite any repeated keys b/c we only need to query for x once, regardless of how many derived fields reference it
      const dependencies = m.requiredProperties.map(rp =>
        METRICS.find(m => m.property === rp)
      );
      await resolveDependencies(
        dependencies,
        measureAggs,
        paramCache,
        fieldsToGrab,
        parentDepth + 1
      );
    }
  }
};

//turns [{field: 'properties.flowId': key: '1234'}, {field: 'properties.flowId': key: '555'}]
//into [{terms: { 'properties.flowId': ['1234', '555'] }}]
const generateTermsFilters = (filters, flattenedMapping) => {
  const termsFilterMap = filters.reduce((acc, cur) => {
    if (!acc[cur.field]) {
      acc[cur.field] = [];
    }
    acc[cur.field].push(cur.key);
    return acc;
  }, {});

  const termsFilters = Object.keys(termsFilterMap).map(f => {
    let filterField = f;
    const mappedFieldType = flattenedMapping[f];

    //if we are trying to do a terms filter on a property that is mapped as both text and keyword, switch to the keyword field
    if (mappedFieldType === 'text') {
      filterField += '.keyword';
    }

    let termsFilter = {
      terms: {
        [filterField]: termsFilterMap[f]
      }
    };

    const nestedField = NESTED_FIELDS.find(nf => nf.field === f);
    if (nestedField) {
      termsFilter = {
        nested: {
          path: nestedField.path,
          query: termsFilter
        }
      };
    }

    return termsFilter;
  });

  return termsFilters;
};

export const getQuery = ({
  range,
  compositeFields = false,
  termsFilters,
  additionalMusts = [],
  additionalMustNots = []
}) => {
  if (Array.isArray(compositeFields)) {
    return {
      size: '0',
      query: {
        bool: {
          must: [
            {
              range: {
                timeStamp: {
                  time_zone: getElasticTimeZoneOffset(),
                  ...range
                }
              }
            },
            {
              bool: {
                should: [...compositeFields]
              }
            },
            ...termsFilters,
            ...additionalMusts
          ],
          must_not: [...additionalMustNots]
        }
      },
      aggs: {}
    };
  } else {
    return {
      size: '0',
      query: {
        bool: {
          must: [
            {
              range: {
                timeStamp: {
                  time_zone: getElasticTimeZoneOffset(),
                  ...range
                }
              }
            },
            ...termsFilters,
            ...additionalMusts
          ],
          must_not: [...additionalMustNots]
        }
      },
      aggs: {}
    };
  }
};

const getFormattedValue = (value, format) => {
  switch (format) {
    case 'currency':
      if (typeof value === 'number') {
        return formatCurrency(value);
      }
      break;
    case 'percent':
      if (typeof value === 'number') {
        return `${(value * 100).toFixed(2)}%`;
      }
      break;
    case 'date':
      if (typeof value === 'string') {
        return new Date(value).toLocaleString();
      }
      break;
    case 'number':
      if (typeof value === 'number') {
        return formatNumber(value);
      }
      break;
    case 'integer':
      if (typeof value === 'number') {
        return value.toFixed();
      }
      break;
    default:
    //noop;
  }
  return value;
};

const trendDirections = {
  UP: 'UP',
  DOWN: 'DOWN'
};

const Trend = ({ current, previous, format }) => {
  let difference;
  let trendDirection = null;
  if (format === 'currency') {
    if (typeof previous === 'number') {
      difference = current - previous;
      trendDirection =
        difference > 0
          ? trendDirections.UP
          : difference < 0
          ? trendDirections.DOWN
          : null;
    }
  } else {
    if (typeof previous === 'number' && previous !== 0) {
      difference = current / previous;
      if (difference < 1) {
        difference = 1 - difference;
      }
      trendDirection =
        difference > 1
          ? trendDirections.UP
          : difference < 1
          ? trendDirections.DOWN
          : null;
    }
  }

  return (
    <Box
      direction='row'
      flex={{ shrink: 0 }}
      align='center'
      title={
        typeof previous !== 'undefined'
          ? `Previous Period Value: ${previous}`
          : null
      }
      className='export-omit'
    >
      {trendDirection === trendDirections.UP ? (
        <CaretUpFill size='small' color='status-ok' />
      ) : trendDirection === trendDirections.DOWN ? (
        <CaretDownFill size='small' color='status-error' />
      ) : null}

      <Text
        size='small'
        color={
          trendDirection === trendDirections.UP
            ? 'status-ok'
            : trendDirection === trendDirections.DOWN
            ? 'status-error'
            : 'inherit'
        }
      >
        {trendDirection !== null &&
          getFormattedValue(
            Math.abs(difference),
            format === 'currency' ? 'currency' : 'percent'
          )}
      </Text>
    </Box>
  );
};

const getColumn = (
  propertyName,
  header,
  title,
  format,
  textAlign = 'start'
) => {
  return {
    property: propertyName,
    header: () => (
      <Text
        title={title || header}
        style={{ whiteSpace: 'normal', fontSize: 'inherit' }}
      >
        {header}
      </Text>
    ),

    render: row => (
      <Box direction='row' justify='between'>
        <Text
          textAlign={textAlign}
          weight={Object.values(row).find(v => v === 'All') ? 'bold' : 'normal'}
          style={{ width: '100%' }}
        >
          {getFormattedValue(row[propertyName], format)}
        </Text>
        {typeof row[`${propertyName}.comparison`] !== 'undefined' && (
          <Trend
            current={row[propertyName]}
            previous={row[`${propertyName}.comparison`]}
            format={format}
          />
        )}
      </Box>
    ),
    sort: propertyName,
    defaultOrder: 'desc',
    format: format
  };
};

const timeStampFormatter = recencyFilter => timestamp => {
  if (timestamp === 'All') {
    return 'All';
  }
  return getFormattedDate(timestamp, recencyFilter);
};

const implementationTypeFormatter = implementationType => {
  if (implementationType === 'All') {
    return 'All';
  }
  return (
    IMPLEMENTATION_TYPES.find(it => it.value === implementationType)?.label ??
    'No Value'
  );
};

const getColumns = (groups, fields, recencyFilter) => {
  const columns = [];
  columns.push(
    ...groups.map(g =>
      groupColumn(
        g,
        groups,
        g === 'timeStamp'
          ? timeStampFormatter(recencyFilter)
          : g === 'timeStampHourly'
          ? timeStampFormatter('HOURLY')
          : g === 'properties.implementationType.keyword'
          ? implementationTypeFormatter
          : null
      )
    )
  );
  columns.push(
    ...fields.map(f => {
      return getColumn(
        f.property,
        f.name,
        f.title,
        f.format,
        f.type === 'measure' ? 'end' : 'start'
      );
    })
  );

  return columns;
};

// We forward the ref given to us from ReportRenderer (again) to pass off to
// the underlying SimpleTable
const MemoTable = memo(
  forwardRef(
    (
      {
        exportReportData,
        sortedReportData,
        columns,
        columnSort,
        setColumnSort,
        loadingData
      },
      ref
    ) => {
      const MIN_COLUMN_WIDTH = 150;
      const [areColumnsLessThanMin, setAreColumnsLessThanMin] = useState(false);

      useEffect(() => {
        exportReportData &&
          exportReportData({
            loadingData,
            exportRows: sortedReportData,
            exportCols: columns
          });
      }, [sortedReportData, columns, loadingData, exportReportData]);

      const measuredRef = useCallback(
        node => {
          if (node !== null) {
            setAreColumnsLessThanMin(
              node.clientWidth < MIN_COLUMN_WIDTH * columns.length
            );
          }
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [columns]
      );
      return (
        <Box fill overflow={{ horizontal: 'auto' }} ref={measuredRef}>
          <SimpleTable
            ref={ref}
            style={
              areColumnsLessThanMin
                ? {
                    width: MIN_COLUMN_WIDTH * columns.length + 'px',
                    maxWidth: 'unset',
                    position: 'relative'
                  }
                : { position: 'relative' }
            }
            data={sortedReportData}
            columns={columns}
            searchFilter={columnSort.field}
            searchOrder={columnSort.direction}
            keyBuilder={r => r.key}
            onHeaderClick={c => {
              if (c.sort) {
                const newColumnSort = {};
                if (c.sort === columnSort.field) {
                  newColumnSort.field = columnSort.field;

                  newColumnSort.direction =
                    columnSort.direction === 'desc' ? 'asc' : 'desc';
                } else {
                  newColumnSort.field = c.sort;
                  newColumnSort.direction = c.defaultOrder;
                }
                setColumnSort(newColumnSort);
              }
            }}
            loadingData={loadingData}
            useVirtualRows={true}
            entityType='report-grid'
          />
        </Box>
      );
    }
  )
);

let reportDataAborter;
let trendDataAborter;

// This is used in both report-viewer and report-viewer;
// We pass the ref through ReportRenderer into MemoTable,
// and subsequently the SimpleTable
const ReportRenderer = forwardRef(
  ({ reportDefinition, addError, exportReportData }, tableRef) => {
    const {
      groups = [],
      fields = [],
      filters = [],
      excludedFilters = [],
      recencyFilter,
      customDatePeriod,
      isPreview,
      showTrend,
      additionalMusts,
      additionalMustNots
    } = reportDefinition;

    //metric data from CDS
    const [reportData, setReportData] = useState([]);

    //metric data from CDS for previous period
    const [comparisonReportData, setComparisonReportData] = useState([]);

    //combination of all data sources to drive the grid
    const [gridData, setGridData] = useState([]);

    //sorted array of gridData
    const [sortedGridData, setSortedGridData] = useState([]);

    const [columns, setColumns] = useState([]);
    const [itemNames, setItemNames] = useState({});
    const [itemInfo, setItemInfo] = useState({});
    const [pageTypes, setPageTypes] = useState({});
    const [sourceInfo, setSourceInfo] = useState({});
    const [columnSort, setColumnSort] = useState(
      groups.length > 0
        ? {
            field: getGroupSortProperty(groups[0]),
            direction: 'desc'
          }
        : {}
    );
    const paramCache = useRef({});
    const [flattenedMapping, setFlattenedMapping] = useState();
    const [loadingCdsData, setLoadingCdsData] = useState(false);
    const [loadingGrid, setLoadingGrid] = useState(true);

    //all metrics required (built by recursively parsing dependencies of fields)
    const [allFieldDependencies, setAllFieldDependencies] = useState([]);
    const location = useLocation();

    useEffect(() => {
      const getIt = async () => {
        setFlattenedMapping(await getFlattenedEventMapping());
        setPageTypes(await getPageTypes());
        setSourceInfo(await getSourceInfo());
      };

      getIt();
    }, []);

    //calculate all dependent data points necessary for derived metrics for any selected fields
    useEffect(() => {
      const allDependencies = new Set();
      fields.forEach(f => {
        const fieldDependencies = getDependencies(f.property);
        //add field child dependencies
        fieldDependencies.forEach(fd => {
          if (!allDependencies.has(fd)) {
            allDependencies.add(fd);
          }
        });

        //add field
        if (!allDependencies.has(f)) {
          allDependencies.add(f);
        }
      });

      setAllFieldDependencies(Array.from(allDependencies));
    }, [fields]);

    useEffect(() => {
      if (allFieldDependencies.length === 0 || !flattenedMapping) {
        setComparisonReportData([]);
        return;
      }

      const dateRange = getRange(customDatePeriod);

      const termsFilters = generateTermsFilters(filters, flattenedMapping);
      const excludedTermsFilters = generateTermsFilters(
        excludedFilters,
        flattenedMapping
      );

      // Combine excluded filters with any additional must_nots
      const combinedMustNots = [
        ...(additionalMustNots || []),
        ...excludedTermsFilters
      ];

      const cdsFields = allFieldDependencies.filter(
        f => f.dataSource === dataSources.CDS
      );

      //we want to make the cds query if user has chosen any metrics (even external), because we use it to drive our unique group buckets
      if (fields.length > 0) {
        if (reportDataAborter) {
          reportDataAborter.abort();
        }
        reportDataAborter = new AbortController();

        if (trendDataAborter) {
          trendDataAborter.abort();
        }
        trendDataAborter = new AbortController();

        if (location.search !== '?old') {
          getCompositeCDSData(
            groups,
            cdsFields,
            termsFilters,
            dateRange,
            getInterval(recencyFilter, groups.includes('timeStampHourly')),
            isPreview,
            createContextEventUrl({
              range: customDatePeriod,
              recency: recencyFilter
            }),
            newReportData => {
              setReportData(newReportData);
              if (showTrend) {
                getCompositeCDSData(
                  groups,
                  cdsFields,
                  termsFilters,
                  getComparisonRange(customDatePeriod),
                  getInterval(
                    recencyFilter,
                    groups.includes('timeStampHourly')
                  ),
                  isPreview,
                  createContextEventUrl({
                    range: elasticComparisonToRange(
                      getComparisonRange(customDatePeriod)
                    )
                  }),
                  setComparisonReportData,
                  () => {},
                  addError,
                  paramCache,
                  trendDataAborter.signal,
                  additionalMusts,
                  combinedMustNots
                );
              }
            },
            setLoadingCdsData,
            addError,
            paramCache,
            reportDataAborter.signal,
            additionalMusts,
            combinedMustNots
          );
        } else {
          getTermsCDSData(
            groups,
            cdsFields,
            termsFilters,
            dateRange,
            getInterval(recencyFilter, groups.includes('timeStampHourly')),
            getBounds(customDatePeriod),
            isPreview,
            createContextEventUrl({
              range: customDatePeriod,
              recency: recencyFilter
            }),
            newReportData => {
              setReportData(newReportData);
              if (showTrend) {
                getTermsCDSData(
                  groups,
                  cdsFields,
                  termsFilters,
                  getComparisonRange(customDatePeriod),
                  getInterval(
                    recencyFilter,
                    groups.includes('timeStampHourly')
                  ),
                  getComparisonBounds(customDatePeriod),
                  isPreview,
                  createContextEventUrl({
                    range: elasticComparisonToRange(
                      getComparisonRange(customDatePeriod)
                    )
                  }),
                  setComparisonReportData,
                  () => {},
                  addError,
                  paramCache,
                  trendDataAborter.signal,
                  additionalMusts,
                  combinedMustNots
                );
              }
            },
            setLoadingCdsData,
            addError,
            paramCache,
            reportDataAborter.signal,
            additionalMusts,
            combinedMustNots
          );
        }
      }
    }, [
      fields.length,
      groups,
      allFieldDependencies,
      filters,
      excludedFilters,
      addError,
      isPreview,
      recencyFilter,
      customDatePeriod,
      showTrend,
      flattenedMapping,
      additionalMusts,
      additionalMustNots,
      location
    ]);

    useEffect(() => {
      //resolve item names when report data changes
      if (groups.length > 0) {
        const allIds = new Set();
        const groupKeys = groups.map(getGroupProperty);
        groupKeys.forEach(gk => {
          reportData.forEach(r => {
            if (typeof r[gk] !== 'undefined' && r[gk] !== 'All') {
              allIds.add(r[gk]);
            }
          });
        });
        if (allIds.size > 0) {
          const ids = Array.from(allIds);
          //if we already have the names for all the ids, don't make the request again
          if (itemNames && ids.every(id => itemNames.hasOwnProperty(id))) {
            return;
          }

          const chunkSize = 10000;
          for (let i = 0; i < ids.length; i += chunkSize) {
            const chunk = ids.slice(i, i + chunkSize);
            // do whatever
            getItemNames(
              Array.from(chunk),
              itemNames => setItemNames(prev => ({ ...prev, ...itemNames })),
              addError
            );

            getItemInfo(
              Array.from(chunk),
              itemInfo => setItemInfo(prev => ({ ...prev, ...itemInfo })),
              addError
            );

            // if grouping by subverticals, get their names
            if (
              groups.some(
                g =>
                  g === 'properties.bannerData.campaignSubVerticalId.keyword' ||
                  g === 'properties.sourceSubVerticalId.keyword' ||
                  g === 'properties.url.queryParams.subpartnersubverticalid'
              )
            ) {
              getSubVerticalNames(
                Array.from(chunk),
                itemNames => setItemNames(prev => ({ ...prev, ...itemNames })),
                addError
              );
            }

            // if grouping by placement experience, get their names
            if (
              groups.some(
                g => g === 'properties.url.queryParams.placementexperienceid'
              )
            ) {
              getPlacementExperienceNames(
                Array.from(chunk),
                itemNames => setItemNames(prev => ({ ...prev, ...itemNames })),
                addError
              );
            }

            // if grouping by placement location, get their names
            if (
              groups.some(
                g => g === 'properties.url.queryParams.placementlocationid'
              )
            ) {
              getPlacementLocationNames(
                Array.from(chunk),
                itemNames => setItemNames(prev => ({ ...prev, ...itemNames })),
                addError
              );
            }
          }
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportData, groups, addError]);

    useEffect(() => {
      // If requesting data from CDS, use that to determine the full grid loader
      // If no fields and no data requested, just hide loader
      if (
        allFieldDependencies.filter(f => f.dataSource === dataSources.CDS)
          .length
      ) {
        setLoadingGrid(loadingCdsData);
      } else {
        setLoadingGrid(false);
      }
    }, [loadingCdsData, allFieldDependencies]);

    //generate aggregate grid data array whenever required inputs change
    useEffect(() => {
      //list of all metrics that need to be executed
      const derivedMetrics = allFieldDependencies.filter(f => f.formula);

      const getSourceIdUrl = (source, sourceId) => {
        return source?.['id']
          ? buildPathForitem({
              id: source['id'],
              type: TRAFFIC_SOURCE
            })
          : `/${TRAFFIC_SOURCE}/${generateNewId()}/info?sourceId=${sourceId}`;
      };

      const newGridData = reportData.map(rD => {
        /// changed this to a reference to the original object instead of copying the values to a new object.
        /// need the values later on in applyDerivedMetricsToRow()
        /// just making a note incase this effs something up.

        const gridRow = rD;

        Object.keys(gridRow).forEach(k => {
          //append resolved names for specific fields
          if (k === 'pageType') {
            gridRow[`${k}.name`] = pageTypes[gridRow[k]];
          } else if (ITEM_GROUP_FIELDS.indexOf(k) !== -1) {
            if (k === 'sortStrategy') {
              gridRow[`${k}.name`] =
                SORT_STRATEGY_NAMES[gridRow[k]] || gridRow[k];
            } else {
              gridRow[`${k}.name`] = itemNames[gridRow[k]] || gridRow[k];
              if (!DO_NOT_SHOW_URL_PATH.includes(k)) {
                gridRow[`${k}.urlPath`] =
                  itemInfo[gridRow[k]] &&
                  buildPathForitem(itemInfo[gridRow[k]]);
              }
            }
          } else if (k === 'sourceid') {
            gridRow[`${k}.name`] = sourceInfo[gridRow[k]]
              ? `${sourceInfo[gridRow[k]]['name']}||${gridRow[k]}`
              : gridRow[k];
            gridRow[`${k}.urlPath`] = getSourceIdUrl(
              sourceInfo[gridRow[k]],
              gridRow[k]
            );
          } else if (k === 'trafficPartnerId') {
            gridRow[`${k}.name`] = itemNames[gridRow[k]] || gridRow[k];
            gridRow[`${k}.urlPath`] =
              itemInfo[gridRow[k]] && buildPathForitem(itemInfo[gridRow[k]]);
          }
        });

        let parentSubTotalRow;
        if (
          groups.some(
            g => !!COPY_DOWN_METRICS.find(({ groupBy }) => groupBy === g)
          ) &&
          !gridRow.isSubtotalRow
        ) {
          parentSubTotalRow = getGridRowParent(gridRow.key, reportData);
        }

        if (derivedMetrics.length > 0) {
          applyDerivedMetricsToRow(derivedMetrics, gridRow, parentSubTotalRow);
        }
        return { ...gridRow };
      });

      setGridData(newGridData);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reportData, itemNames, pageTypes, allFieldDependencies]);

    useEffect(() => {
      //clear these out when groups or fields changes otherwise it will render with old data, perhaps causing duplicate key
      setReportData([]);
      setSortedGridData([]);

      const newColumns = getColumns(groups, fields, recencyFilter);
      setColumns(newColumns);

      //removed the field that we were sorting by, clear out the sort order
      if (!newColumns.find(c => c.sort === columnSort.field)) {
        setColumnSort(
          groups.length > 0
            ? {
                field: getGroupSortProperty(groups[0]),
                direction: 'desc'
              }
            : {}
        );
      }

      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [groups, fields, recencyFilter, filters]);

    //sort the report data when it changes
    useEffect(() => {
      //no sort defined, just use the data as it is
      if (!columnSort.field) {
        setSortedGridData(gridData);
        return;
      }

      let groupProperties = groups.map(getGroupProperty);
      //use magic group sorting
      if (groupProperties.length > 0) {
        const sortedGroupRows = getSortedGroupRows(
          gridData,
          groupProperties,
          columnSort
        );

        setSortedGridData(sortedGroupRows);
      } else {
        //just use plain old row sorting
        setSortedGridData(
          orderBy(
            gridData,
            row =>
              typeof row[columnSort.field] !== 'undefined'
                ? row[columnSort.field]
                : '',
            [columnSort.direction]
          )
        );
      }
    }, [columnSort, gridData, groups]);

    useEffect(() => {
      if (comparisonReportData.length > 0) {
        const combinedReportData = [...reportData];
        combinedReportData.forEach(crd => {
          const matchingComparisonRow = comparisonReportData.find(
            comp => comp.key === crd.key
          );
          if (matchingComparisonRow) {
            Object.keys(crd)
              .filter(k => k !== 'key')
              .forEach(k => {
                crd[`${k}.comparison`] = matchingComparisonRow[k];
              });
          }
        });
        setReportData(combinedReportData);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [comparisonReportData]);
    return (
      <Box fill>
        <MemoTable
          exportReportData={exportReportData}
          ref={tableRef}
          sortedReportData={sortedGridData}
          columns={columns}
          columnSort={columnSort}
          setColumnSort={setColumnSort}
          loadingData={loadingGrid}
        />
      </Box>
    );
  }
);

ReportRenderer.displayName = 'ReportRenderer';

export default ReportRenderer;
